import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Download, Loader2, Palette, CheckCircle2, Upload, X } from "lucide-react";

export default function OnePager() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [a, setA] = useState(null);
  const [brand, setBrand] = useState({ studio_name: "", brand_color: "#002FA7", tagline: "" });
  const [savingBrand, setSavingBrand] = useState(false);
  const [compliance, setCompliance] = useState(null);

  useEffect(() => {
    Promise.all([api.get(`/readiness/${id}`), api.get("/settings/brand")]).then(([r, b]) => {
      setA(r.data); setBrand(b.data);
    });
  }, [id]);

  useEffect(() => {
    if (!a?.client_name) return;
    const loadCompliance = async () => {
      try {
        let clientId = a.client_id;
        if (!clientId) {
          const { data: clients } = await api.get("/clients");
          const match = (clients || []).find((c) => c.name === a.client_name);
          clientId = match?.id;
        }
        if (!clientId) { setCompliance(null); return; }
        const { data } = await api.get(`/compliance/assessment/${clientId}`);
        const answered = Object.values(data.scores || {}).some((s) => s.answered > 0);
        setCompliance(answered ? data.scores : null);
      } catch { setCompliance(null); }
    };
    loadCompliance();
  }, [a?.client_name, a?.client_id]);

  const saveBrand = async () => {
    setSavingBrand(true);
    try { const { data } = await api.put("/settings/brand", brand); setBrand(data); toast.success("Branding salvato"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingBrand(false); }
  };

  const onLogoFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Seleziona un file immagine"); return; }
    if (file.size > 700 * 1024) { toast.error("Logo troppo grande (max 700KB)"); return; }
    const reader = new FileReader();
    reader.onload = () => setBrand((b) => ({ ...b, logo: reader.result }));
    reader.readAsDataURL(file);
  };

  if (!a) return <Layout><PageHeader title="One-page" /><div className="flex justify-center py-24"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div></Layout>;

  const c = brand.brand_color || "#002FA7";
  const d = a.ai_deliverable || {};
  const kpis = (a.kpis || []).filter((k) => (k.improvement || 0) !== 0);

  return (
    <Layout>
      <PageHeader title="Executive One-Pager" subtitle={a.client_name}>
        <Button variant="outline" className="rounded-none" onClick={() => navigate(`/readiness/${id}`)}><ArrowLeft className="w-4 h-4 mr-2" /> Indietro</Button>
        <Button className="rounded-none" data-testid="onepager-pdf-btn" onClick={() => window.open(`${API}/readiness/${id}/onepager.pdf`, "_blank")}><Download className="w-4 h-4 mr-2" /> Scarica PDF</Button>
      </PageHeader>

      <div className="p-8 grid grid-cols-1 lg:grid-cols-4 gap-6">
        {isAdmin && (
          <div className="bg-white border border-border p-5 h-fit" data-testid="brand-editor">
            <div className="flex items-center gap-2 data-label mb-3"><Palette className="w-3.5 h-3.5 text-primary" /> Branding studio</div>
            <div className="space-y-3">
              <div><Label className="data-label">Nome studio</Label><Input data-testid="brand-name-input" className="mt-1 rounded-none" value={brand.studio_name} onChange={(e) => setBrand({ ...brand, studio_name: e.target.value })} /></div>
              <div>
                <Label className="data-label">Logo studio</Label>
                <div className="mt-1 flex items-center gap-3">
                  {brand.logo ? (
                    <div className="relative border border-border p-1 bg-white">
                      <img src={brand.logo} alt="logo" className="h-10 w-auto max-w-[120px] object-contain" data-testid="brand-logo-preview" />
                      <button type="button" onClick={() => setBrand({ ...brand, logo: "" })} data-testid="brand-logo-remove" className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-0.5"><X className="w-3 h-3" /></button>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-border text-sm text-muted-foreground cursor-pointer hover:border-primary hover:text-foreground" data-testid="brand-logo-upload">
                      <Upload className="w-4 h-4" /> Carica logo
                      <input type="file" accept="image/*" className="hidden" onChange={onLogoFile} />
                    </label>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">PNG/JPG, max 700KB. Usato nei PDF (one-pager e report).</p>
              </div>
              <div><Label className="data-label">Tagline</Label><Input className="mt-1 rounded-none" value={brand.tagline || ""} onChange={(e) => setBrand({ ...brand, tagline: e.target.value })} /></div>
              <div><Label className="data-label">Partner (co-branding)</Label><Input data-testid="brand-partner-input" className="mt-1 rounded-none" value={brand.partner || ""} onChange={(e) => setBrand({ ...brand, partner: e.target.value })} placeholder="es. DevRev" /><p className="text-[11px] text-muted-foreground mt-1">Se compilato, appare come "In collaborazione con …" nei PDF.</p></div>
              <div>
                <Label className="data-label">Colore brand</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input type="color" value={c} onChange={(e) => setBrand({ ...brand, brand_color: e.target.value })} className="w-10 h-9 border border-border cursor-pointer" data-testid="brand-color-input" />
                  <Input className="rounded-none font-mono" value={brand.brand_color} onChange={(e) => setBrand({ ...brand, brand_color: e.target.value })} />
                </div>
              </div>
              <Button className="rounded-none w-full" onClick={saveBrand} disabled={savingBrand} data-testid="save-brand-btn">{savingBrand && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salva branding</Button>
            </div>
          </div>
        )}

        {/* A4-like preview */}
        <div className={isAdmin ? "lg:col-span-3" : "lg:col-span-4"}>
          <div className="bg-white border border-border mx-auto shadow-sm" style={{ maxWidth: 820 }} data-testid="onepager-preview">
            <div className="flex items-center justify-between px-8 py-6" style={{ backgroundColor: c }}>
              <div className="flex items-center gap-3">
                {brand.logo && <img src={brand.logo} alt="logo" className="h-8 w-auto max-w-[120px] object-contain bg-white/90 p-1" />}
                <div className="text-white font-heading font-black text-xl tracking-tight">{brand.studio_name || "AI & Sales Readiness"}</div>
              </div>
              <div className="text-white/80 text-xs tracking-[0.2em] uppercase">Executive One-Pager</div>
            </div>

            <div className="p-8 space-y-6">
              <div className="text-sm text-muted-foreground">
                <span className="font-semibold text-foreground">{a.client_name}</span> · {a.sector || "—"} · {new Date().toLocaleDateString("it-IT")}
              </div>

              <div className="flex gap-6 items-center">
                <div className="border-2 text-center px-6 py-4" style={{ borderColor: c }}>
                  <div className="font-mono font-black text-5xl" style={{ color: c }}>{a.overall_score}</div>
                  <div className="data-label mt-1">Score /100</div>
                </div>
                <div className="flex-1 space-y-1">
                  {(a.breakdown || []).map((b) => (
                    <div key={b.key} className="flex items-center gap-2 text-sm">
                      <span className="w-40 text-muted-foreground truncate">{b.name}</span>
                      <div className="flex-1 h-2 bg-secondary"><div className="h-2" style={{ width: `${(b.score / 5) * 100}%`, backgroundColor: c }} /></div>
                      <span className="font-mono text-xs w-8 text-right">{b.score}/5</span>
                    </div>
                  ))}
                </div>
              </div>

              {compliance && (
                <div data-testid="onepager-compliance-badge">
                  <div className="font-heading font-bold text-sm mb-1" style={{ color: c }}>CONFORMITÀ · NIS2 · DORA · GDPR · OT</div>
                  <div className="grid grid-cols-4 gap-2">
                    {["NIS2", "DORA", "GDPR", "OT"].map((fw) => {
                      const s = compliance[fw] || { score: 0, level: "n/d" };
                      const col = s.score >= 80 ? "#00A859" : s.score >= 50 ? "#F59E0B" : "#DC2626";
                      return (
                        <div key={fw} className="border border-border p-3 text-center">
                          <div className="data-label">{fw}</div>
                          <div className="font-mono font-black text-2xl" style={{ color: col }}>{s.score}%</div>
                          <div className="text-[10px]" style={{ color: col }}>{s.level}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {d.summary && <div><div className="font-heading font-bold text-sm mb-1" style={{ color: c }}>SINTESI</div><p className="text-sm">{d.summary}</p></div>}

              {d.top_priorities?.length > 0 && (
                <div>
                  <div className="font-heading font-bold text-sm mb-1" style={{ color: c }}>PRIORITÀ (QUICK WIN)</div>
                  <ul className="text-sm space-y-1 list-disc pl-5">
                    {d.top_priorities.slice(0, 3).map((p, i) => <li key={i}><b>{p.title}</b> — <span className="text-muted-foreground">{p.why}</span></li>)}
                  </ul>
                </div>
              )}

              {kpis.length > 0 && (
                <div>
                  <div className="font-heading font-bold text-sm mb-1" style={{ color: c }}>RISULTATI MISURATI</div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {kpis.slice(0, 4).map((k) => (
                      <div key={k.id} className="border border-border p-3">
                        <div className="text-xs text-muted-foreground truncate">{k.name}</div>
                        <div className="font-mono font-bold text-[#00A859]">+{k.improvement} <span className="text-xs">{k.unit}</span>{k.improvement_pct != null && <span className="text-xs"> ({k.improvement_pct > 0 ? "+" : ""}{k.improvement_pct}%)</span>}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {d.recommended_offer && (
                <div className="border-l-4 p-4 bg-secondary/40" style={{ borderColor: c }}>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4" style={{ color: c }} />
                    <span><b>Next step:</b> {d.recommended_offer.level} — <span className="text-muted-foreground">{d.recommended_offer.reason}</span></span>
                  </div>
                </div>
              )}

              {!d.summary && (
                <p className="text-sm text-muted-foreground border border-dashed border-border p-4">
                  Genera prima il deliverable AI nella scheda cliente per popolare sintesi, priorità e next step della one-page.
                </p>
              )}

              <div className="text-[10px] text-muted-foreground pt-4 border-t border-border" data-testid="onepager-footer">
                {brand.studio_name || "AI & Sales Readiness"}{brand.tagline ? ` — ${brand.tagline}` : ""}
                {brand.partner ? <div className="mt-0.5">In collaborazione con {brand.partner}</div> : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
